import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { Plus, FileText, Send, Trash2, Download, Clock, Check, Loader2, X, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const OneOffInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sendingBatch, setSendingBatch] = useState(false);

  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    site_address: '',
    bill_to: '',
    service_description: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0 }],
    vat_applicable: false,
    vat_rate: 20,
    payment_terms: 14,
    notes: '',
    scheduled_send_date: ''
  });

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/oneoff/invoices`);
      const data = await res.json();
      setInvoices(data.invoices || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({ ...prev, line_items: newItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_price: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      setFormData(prev => ({
        ...prev,
        line_items: prev.line_items.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotal = () => {
    const subtotal = formData.line_items.reduce((sum, item) => 
      sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0);
    const vat = formData.vat_applicable ? subtotal * (formData.vat_rate / 100) : 0;
    return { subtotal, vat, total: subtotal + vat };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        line_items: formData.line_items.map(item => ({
          ...item,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        payment_terms: parseInt(formData.payment_terms) || 14,
        scheduled_send_date: formData.scheduled_send_date || null
      };
      
      const res = await fetch(`${API_URL}/api/admin/oneoff/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setShowForm(false);
        setFormData({
          client_name: '',
          client_email: '',
          site_address: '',
          bill_to: '',
          service_description: '',
          line_items: [{ description: '', quantity: 1, unit_price: 0 }],
          vat_applicable: false,
          vat_rate: 20,
          payment_terms: 14,
          notes: '',
          scheduled_send_date: ''
        });
        fetchInvoices();
      }
    } catch (err) {
      console.error('Failed to create invoice:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    setSendingId(invoiceId);
    try {
      const res = await fetch(`${API_URL}/api/admin/oneoff/invoices/${invoiceId}/send`, { method: 'POST' });
      if (res.ok) {
        fetchInvoices();
        if (selectedInvoice?.id === invoiceId) {
          const updated = await res.json();
          setSelectedInvoice(prev => ({ ...prev, status: 'sent' }));
        }
      }
    } catch (err) {
      console.error('Failed to send invoice:', err);
    } finally {
      setSendingId(null);
    }
  };

  const handleSendBatch = async () => {
    if (selectedIds.length === 0) return;
    
    setSendingBatch(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/oneoff/invoices/send-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedIds)
      });
      
      if (res.ok) {
        const data = await res.json();
        alert(data.message);
        setSelectedIds([]);
        fetchInvoices();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to send batch');
      }
    } catch (err) {
      console.error('Failed to send batch:', err);
      alert('Failed to send batch');
    } finally {
      setSendingBatch(false);
    }
  };

  const toggleSelectInvoice = (invoiceId) => {
    setSelectedIds(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const selectAllUnsent = () => {
    const unsentIds = invoices
      .filter(inv => inv.status !== 'sent' && inv.status !== 'paid')
      .map(inv => inv.id);
    setSelectedIds(unsentIds);
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/oneoff/invoices/${invoiceId}/status?status=paid`, { method: 'PUT' });
      if (res.ok) {
        fetchInvoices();
        if (selectedInvoice?.id === invoiceId) {
          setSelectedInvoice(prev => ({ ...prev, status: 'paid' }));
        }
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/oneoff/invoices/${invoiceId}`, { method: 'DELETE' });
      if (res.ok) {
        setSelectedInvoice(null);
        fetchInvoices();
      }
    } catch (err) {
      console.error('Failed to delete invoice:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-600',
      scheduled: 'bg-purple-100 text-purple-700',
      sent: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700'
    };
    return styles[status] || styles.draft;
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];
  const totals = calculateTotal();

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </span>
            One-Off Invoices
          </h1>
          <p className="text-gray-500 text-sm mt-1">Standalone invoices with optional scheduling</p>
        </div>
        <Button 
          onClick={() => setShowForm(true)}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="create-oneoff-invoice-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {/* Create Invoice Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Create One-Off Invoice</h2>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Client Details */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 text-sm uppercase tracking-wide">Client Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 mb-1.5 block">Client Name *</Label>
                    <Input
                      name="client_name"
                      value={formData.client_name}
                      onChange={handleInputChange}
                      required
                      className="h-10"
                      placeholder="ABC Property Management"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 mb-1.5 block">Client Email *</Label>
                    <Input
                      name="client_email"
                      type="email"
                      value={formData.client_email}
                      onChange={handleInputChange}
                      required
                      className="h-10"
                      placeholder="accounts@example.com"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5 block">Site Address * <span className="text-gray-400 font-normal">(where cleaning was done)</span></Label>
                  <textarea
                    name="site_address"
                    value={formData.site_address}
                    onChange={handleInputChange}
                    required
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-green-500 outline-none resize-none"
                    placeholder="123 High Street, London, SW1A 1AA"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5 block">Bill To <span className="text-gray-400 font-normal">(manual entry - who to address invoice to)</span></Label>
                  <textarea
                    name="bill_to"
                    value={formData.bill_to}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-green-500 outline-none resize-none"
                    placeholder="FAO: Accounts Department&#10;ABC Property Management Ltd&#10;456 Business Park, London EC1A 1BB"
                  />
                </div>
              </div>

              {/* Service Description */}
              <div>
                <Label className="text-gray-700 mb-1.5 block">Service Description <span className="text-gray-400 font-normal">(optional)</span></Label>
                <textarea
                  name="service_description"
                  value={formData.service_description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-green-500 outline-none resize-none"
                  placeholder="One-off deep clean of communal areas"
                />
              </div>

              {/* Line Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 text-sm uppercase tracking-wide">Invoice Items</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                
                {formData.line_items.map((item, index) => (
                  <div key={index} className="flex gap-3 items-start bg-gray-50 p-3 rounded-lg">
                    <div className="flex-1">
                      <Input
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        placeholder="Description"
                        className="h-9"
                      />
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                        className="h-9"
                        min="1"
                        step="0.01"
                      />
                    </div>
                    <div className="w-28">
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <Input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                          placeholder="Price"
                          className="h-9 pl-6"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                    <div className="w-24 text-right pt-2 font-medium text-gray-700">
                      £{((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                    </div>
                    {formData.line_items.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeLineItem(index)}
                        className="p-2 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* VAT and Payment Terms */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                  <input
                    type="checkbox"
                    name="vat_applicable"
                    checked={formData.vat_applicable}
                    onChange={handleInputChange}
                    className="w-5 h-5 rounded border-gray-300 text-green-500 focus:ring-green-500"
                  />
                  <div>
                    <Label className="text-gray-700">VAT Applicable</Label>
                    <p className="text-xs text-gray-500">Add {formData.vat_rate}% VAT to total</p>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5 block">Payment Terms (days)</Label>
                  <Input
                    name="payment_terms"
                    type="number"
                    value={formData.payment_terms}
                    onChange={handleInputChange}
                    className="h-10"
                    min="1"
                    max="90"
                  />
                </div>
              </div>

              {/* Scheduling */}
              <div className="bg-purple-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  <h3 className="font-medium text-gray-900">Schedule Send</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Set a date to automatically send this invoice. The invoice date will be set to the send date.
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label className="text-gray-700 mb-1.5 block">Send Date <span className="text-gray-400 font-normal">(leave empty to send manually)</span></Label>
                    <Input
                      name="scheduled_send_date"
                      type="date"
                      value={formData.scheduled_send_date}
                      onChange={handleInputChange}
                      min={today}
                      className="h-10"
                    />
                  </div>
                  {formData.scheduled_send_date && (
                    <div className="pt-6">
                      <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                        Scheduled for {formatDate(formData.scheduled_send_date)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-gray-700 mb-1.5 block">Notes <span className="text-gray-400 font-normal">(optional)</span></Label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-green-500 outline-none resize-none"
                  placeholder="Any additional notes..."
                />
              </div>

              {/* Totals */}
              <div className="bg-green-50 rounded-xl p-4">
                <div className="flex justify-between text-gray-600 mb-2">
                  <span>Subtotal:</span>
                  <span>£{totals.subtotal.toFixed(2)}</span>
                </div>
                {formData.vat_applicable ? (
                  <div className="flex justify-between text-gray-600 mb-2">
                    <span>VAT ({formData.vat_rate}%):</span>
                    <span>£{totals.vat.toFixed(2)}</span>
                  </div>
                ) : (
                  <div className="flex justify-between text-gray-600 mb-2">
                    <span>VAT:</span>
                    <span className="text-sm">Not applicable (not VAT registered)</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold text-green-700 pt-2 border-t border-green-200">
                  <span>Total:</span>
                  <span>£{totals.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  {formData.scheduled_send_date ? 'Schedule Invoice' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h2 className="text-lg font-semibold text-white">{selectedInvoice.invoice_number}</h2>
                <p className="text-green-100 text-sm">
                  {selectedInvoice.invoice_date ? formatDate(selectedInvoice.invoice_date) : 'Not sent yet'}
                </p>
              </div>
              <button onClick={() => setSelectedInvoice(null)} className="p-2 hover:bg-white/20 rounded-lg text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status and Actions */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusBadge(selectedInvoice.status)}`}>
                    {selectedInvoice.status}
                  </span>
                  {selectedInvoice.is_scheduled && selectedInvoice.status === 'scheduled' && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {formatDate(selectedInvoice.scheduled_send_date)}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <a
                    href={`${API_URL}/api/admin/oneoff/invoices/${selectedInvoice.id}/download-pdf`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4" /> PDF
                  </a>
                  {selectedInvoice.status !== 'paid' && (
                    <Button
                      size="sm"
                      onClick={() => handleSendInvoice(selectedInvoice.id)}
                      disabled={sendingId === selectedInvoice.id}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {sendingId === selectedInvoice.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      {selectedInvoice.status === 'sent' ? 'Resend' : 'Send Now'}
                    </Button>
                  )}
                  {selectedInvoice.status === 'sent' && (
                    <Button size="sm" onClick={() => handleMarkPaid(selectedInvoice.id)} className="bg-emerald-600 hover:bg-emerald-700">
                      <Check className="w-4 h-4 mr-1" /> Mark Paid
                    </Button>
                  )}
                </div>
              </div>

              {/* Bill To */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">BILL TO</h3>
                {selectedInvoice.bill_to ? (
                  <p className="text-gray-900 whitespace-pre-line">{selectedInvoice.bill_to}</p>
                ) : (
                  <p className="text-gray-900">{selectedInvoice.client_name}</p>
                )}
              </div>

              {/* Site Address */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">SITE ADDRESS</h3>
                <p className="text-gray-900 whitespace-pre-line">{selectedInvoice.site_address || 'N/A'}</p>
                <p className="text-gray-600 text-sm mt-1">{selectedInvoice.client_email}</p>
              </div>

              {/* Service Description */}
              {selectedInvoice.service_description && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">SERVICE</h3>
                  <p className="text-gray-900">{selectedInvoice.service_description}</p>
                </div>
              )}

              {/* Line Items */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">ITEMS</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-medium text-gray-600">Description</th>
                        <th className="text-right px-4 py-2 text-sm font-medium text-gray-600">Qty</th>
                        <th className="text-right px-4 py-2 text-sm font-medium text-gray-600">Price</th>
                        <th className="text-right px-4 py-2 text-sm font-medium text-gray-600">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedInvoice.line_items || []).map((item, idx) => (
                        <tr key={idx} className="border-t border-gray-100">
                          <td className="px-4 py-2 text-gray-900">{item.description}</td>
                          <td className="px-4 py-2 text-gray-600 text-right">{item.quantity}</td>
                          <td className="px-4 py-2 text-gray-600 text-right">£{(item.unit_price || 0).toFixed(2)}</td>
                          <td className="px-4 py-2 text-gray-900 text-right font-medium">£{(item.total || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between text-gray-600 mb-2">
                  <span>Subtotal:</span>
                  <span>£{(selectedInvoice.subtotal || 0).toFixed(2)}</span>
                </div>
                {selectedInvoice.vat_applicable ? (
                  <div className="flex justify-between text-gray-600 mb-2">
                    <span>VAT ({selectedInvoice.vat_rate}%):</span>
                    <span>£{(selectedInvoice.vat_amount || 0).toFixed(2)}</span>
                  </div>
                ) : (
                  <div className="flex justify-between text-gray-600 mb-2">
                    <span>VAT:</span>
                    <span className="text-sm">Not applicable (not VAT registered)</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold text-green-700 pt-2 border-t border-gray-200">
                  <span>Total:</span>
                  <span>£{(selectedInvoice.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Info */}
              {selectedInvoice.due_date && (
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong>Due Date:</strong> {formatDate(selectedInvoice.due_date)}
                  </p>
                </div>
              )}

              {/* Notes */}
              {selectedInvoice.notes && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">NOTES</h3>
                  <p className="text-gray-600">{selectedInvoice.notes}</p>
                </div>
              )}

              {/* Delete */}
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleDeleteInvoice(selectedInvoice.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete Invoice
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoices List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-green-500" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No One-Off Invoices Yet</h3>
          <p className="text-gray-500 mb-6">Create your first standalone invoice</p>
          <Button onClick={() => setShowForm(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" /> Create Invoice
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {/* Batch actions bar */}
          {selectedIds.length > 0 && (
            <div className="bg-green-50 border-b border-green-200 px-6 py-3 flex items-center justify-between">
              <span className="text-green-700 font-medium">
                {selectedIds.length} invoice{selectedIds.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setSelectedIds([])} 
                  className="text-sm text-green-600 hover:underline"
                >
                  Clear selection
                </button>
                <Button
                  onClick={handleSendBatch}
                  disabled={sendingBatch}
                  className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                >
                  {sendingBatch ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send All ({selectedIds.length})
                </Button>
              </div>
            </div>
          )}
          
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="w-12 px-4 py-4">
                  <button 
                    onClick={selectAllUnsent}
                    className="text-xs text-gray-500 hover:text-green-600"
                    title="Select all unsent"
                  >
                    All
                  </button>
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Invoice</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Site</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Date / Scheduled</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-600">Amount</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-600">Status</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id} className={`border-b border-gray-100 hover:bg-gray-50 ${selectedIds.includes(invoice.id) ? 'bg-green-50' : ''}`}>
                  <td className="px-4 py-4">
                    {invoice.status !== 'sent' && invoice.status !== 'paid' && (
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(invoice.id)}
                        onChange={() => toggleSelectInvoice(invoice.id)}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button 
                      onClick={() => setSelectedInvoice(invoice)}
                      className="font-medium text-green-600 hover:text-green-700"
                    >
                      {invoice.invoice_number}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-gray-900 truncate max-w-[200px]" title={invoice.site_address || invoice.client_name}>
                      {(invoice.site_address || '').split('\n')[0] || invoice.client_name}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-[200px]">{invoice.client_name}</div>
                  </td>
                  <td className="px-6 py-4">
                    {invoice.invoice_date ? (
                      <span className="text-gray-600">{formatDate(invoice.invoice_date)}</span>
                    ) : invoice.scheduled_send_date ? (
                      <span className="text-purple-600 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(invoice.scheduled_send_date)}
                      </span>
                    ) : (
                      <span className="text-gray-400">Not scheduled</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">£{(invoice.total_amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        title="View"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleSendInvoice(invoice.id)}
                        disabled={sendingId === invoice.id || invoice.status === 'paid'}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50"
                        title="Send"
                      >
                        {sendingId === invoice.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </button>
                      <a
                        href={`${API_URL}/api/admin/oneoff/invoices/${invoice.id}/download-pdf`}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleDeleteInvoice(invoice.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OneOffInvoices;
